import { useState, useEffect, useRef, useCallback } from 'react';
import { Icon, Button } from '@atoms/index';
import SearchResultsDropdown from './SearchResultsDropdown';
import { catalogService } from '@services/catalogService';
import type { SearchRestaurantHit, SearchProductHit } from '@/types';

interface HeroSectionProps {
  onLocationRequest?: () => void;
  isLoading?: boolean;
  hasServiceZone?: boolean;
  serviceZoneId?: string;
  deliveryLatitude?: number;
  deliveryLongitude?: number;
}

const HeroSection = ({
  onLocationRequest,
  isLoading = false,
  hasServiceZone = false,
  serviceZoneId,
  deliveryLatitude,
  deliveryLongitude,
}: HeroSectionProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [restaurants, setRestaurants] = useState<SearchRestaurantHit[]>([]);
  const [products, setProducts] = useState<SearchProductHit[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const performSearch = useCallback(async (query: string) => {
    if (query.length < 2) {
      setRestaurants([]);
      setProducts([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    setShowResults(true);

    try {
      const result = await catalogService.search(query, {
        serviceZoneId,
        type: 'all',
        limit: 5,
        latitude: deliveryLatitude,
        longitude: deliveryLongitude,
      });

      setRestaurants(result.restaurants?.hits || []);
      setProducts(result.products?.hits || []);
    } catch {
      setRestaurants([]);
      setProducts([]);
    } finally {
      setIsSearching(false);
    }
  }, [serviceZoneId, deliveryLatitude, deliveryLongitude]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (searchQuery.length < 2) {
      setShowResults(false);
      setRestaurants([]);
      setProducts([]);
      return;
    }

    debounceRef.current = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery, performSearch]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.length >= 2) {
      performSearch(searchQuery);
    }
  };

  const handleClose = () => {
    setShowResults(false);
  };

  return (
    <section className="relative z-20 w-full min-h-[100vh] overflow-visible rounded-b-3xl">
      <div className="absolute inset-0 w-full min-h-full -z-0 overflow-hidden rounded-b-3xl">
        <img
          src="https://images.unsplash.com/photo-1555939594-58d7cb561ad1?q=80&w=1920&auto=format&fit=crop"
          alt=""
          className="absolute inset-0 w-full h-full min-h-[100vh] object-cover object-center"
          loading="eager"
        />
        <div className="absolute inset-0 bg-black/55 z-[1]" aria-hidden />
      </div>

      <div className="relative z-[10] flex flex-col items-center justify-center min-h-[100vh] py-16 sm:py-20 px-4 text-center">
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 leading-tight drop-shadow-md [text-shadow:_2px_2px_8px_rgba(0,0,0,0.8)]">
          Sadece Yemek Değil,
          <br />
          <span className="text-primary-300">Bir Tutku.</span>
        </h1>

        <h2 className="text-lg md:text-xl lg:text-2xl text-white/95 mb-8 max-w-3xl mx-auto font-medium drop-shadow-md [text-shadow:_1px_1px_4px_rgba(0,0,0,0.7)] leading-relaxed">
          Adana'nın ateşi ve şehrin en seçkin lezzetleri, dakikalar içinde kapında.
        </h2>

        <div ref={containerRef} className="w-full max-w-2xl mx-auto relative">
          <form onSubmit={handleSearch}>
            <div className="relative shadow-xl rounded-2xl overflow-visible">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => {
                  if (searchQuery.length >= 2 && (restaurants.length > 0 || products.length > 0)) {
                    setShowResults(true);
                  }
                }}
                placeholder="Restoran, yemek veya mutfak ara..."
                className="w-full h-16 md:h-20 px-6 md:px-8 text-lg bg-white/95 backdrop-blur-md border-2 border-white/50 rounded-2xl focus:outline-none focus:ring-4 focus:ring-primary/30 focus:border-primary transition-all placeholder:text-gray-400 text-gray-900"
              />

              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-12 md:h-16 w-12 md:w-16 bg-primary hover:bg-primary-600 text-white rounded-xl flex items-center justify-center transition-all shadow-lg hover:shadow-xl hover:scale-105"
                aria-label="Ara"
              >
                <Icon name="search" size={24} className="md:w-6 md:h-6" />
              </button>
            </div>
          </form>

          <SearchResultsDropdown
            restaurants={restaurants}
            products={products}
            query={searchQuery}
            isLoading={isSearching}
            isVisible={showResults}
            onClose={handleClose}
            anchorRef={containerRef}
          />
        </div>

        {!hasServiceZone && onLocationRequest && (
          <div className="mt-8 animate-fade-in">
            <Button
              size="lg"
              variant="primary"
              onClick={onLocationRequest}
              isLoading={isLoading}
              className="shadow-xl bg-white/10 backdrop-blur-md hover:bg-white/20 text-white border-2 border-white/30 px-8 py-4 text-lg"
            >
              <Icon name="location" size={24} className="mr-2" />
              {isLoading ? 'Konum Alınıyor...' : '📍 Konumumu Kullan'}
            </Button>
          </div>
        )}

        <div className="mt-8 md:mt-10 flex flex-wrap items-center justify-center gap-4 md:gap-6 text-white/80 text-xs md:text-sm">
          <div className="flex items-center gap-1.5 md:gap-2">
            <Icon name="clock" size={16} className="md:w-5 md:h-5 text-primary-300" />
            <span>30 Dakika Teslimat</span>
          </div>
          <div className="hidden md:block w-1 h-1 rounded-full bg-white/40" />
          <div className="flex items-center gap-1.5 md:gap-2">
            <Icon name="star" size={16} className="md:w-5 md:h-5 text-accent-400" />
            <span>Premium Kalite</span>
          </div>
          <div className="hidden md:block w-1 h-1 rounded-full bg-white/40" />
          <div className="flex items-center gap-1.5 md:gap-2">
            <Icon name="shield" size={16} className="md:w-5 md:h-5 text-secondary-400" />
            <span>Güvenli Ödeme</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
